import React from 'react';

/**
 * KPITooltip — wraps a label with a ? icon that shows a definition on hover.
 * Usage: <KPITooltip term="Savings Rate" def="The percentage by which..." />
 */
export default function KPITooltip({ term, def, glossaryId }) {
  return (
    <span className="kpi-tooltip-wrap">
      {term}
      <span className="kpi-tooltip-icon" aria-label={`Definition of ${term}`}>?</span>
      <span className="kpi-tooltip-popup">
        <strong style={{ display: 'block', marginBottom: 3, color: '#42BA97' }}>{term}</strong>
        {def}
        {glossaryId && (
          <span style={{ display: 'block', marginTop: 4, color: '#42BA97', fontSize: 10 }}>
            → See Glossary for full definition
          </span>
        )}
      </span>
    </span>
  );
}

// Pre-defined tooltip content for common MSSP metrics
export const TOOLTIPS = {
  savings_rate: {
    term: 'Savings Rate',
    def: '(Benchmark − Actual Expense) ÷ Benchmark. Positive = ACO spent below target. Negative = overspent.',
  },
  earned_savings: {
    term: 'Earned Savings',
    def: 'The shared savings payment CMS pays to the ACO. Gross savings × quality score multiplier × sharing rate.',
  },
  generated_savings: {
    term: 'Generated Savings',
    def: 'Gross savings before quality adjustment — total amount ACO spent below benchmark.',
  },
  pbpm: {
    term: 'PBPM',
    def: 'Per Beneficiary Per Month. Total annual expense ÷ beneficiary-months. Normalizes spending for comparison across ACOs of different sizes.',
  },
  raf: {
    term: 'Composite RAF',
    def: 'Risk Adjustment Factor. A score reflecting population health complexity. 1.0 = average Medicare risk. Higher = sicker population.',
  },
  qual_score: {
    term: 'Quality Score',
    def: 'Composite quality performance score (0–100). Determines the quality multiplier applied to shared savings. Must meet QPS threshold to earn full savings.',
  },
  qps: {
    term: 'QPS',
    def: 'Quality Performance Standard. The minimum quality score threshold required for full shared savings eligibility.',
  },
  track_one_sided: {
    term: 'One-Sided Risk',
    def: 'ACO shares in savings but owes nothing if costs exceed benchmark. Max sharing rate ~50%. Basic tracks A and B.',
  },
  track_two_sided: {
    term: 'Two-Sided Risk',
    def: 'ACO shares in both savings (upside) and losses (downside). Higher sharing rates (up to 75–85%) in exchange for downside accountability.',
  },
  track_enhanced: {
    term: 'Enhanced Track',
    def: 'Highest-risk MSSP option. Up to 75% savings sharing, up to 40% loss sharing. Includes SNF waiver and broader benefit flexibilities.',
  },
  pct_earning: {
    term: '% Earning Savings',
    def: 'Share of ACOs that received a shared savings payment from CMS for the performance year.',
  },
  benchmark: {
    term: 'Benchmark',
    def: 'The risk-adjusted spending target an ACO must beat to generate savings. Derived from 3 historical benchmark years, adjusted for regional trends.',
  },
};
